import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { money, fmtDate, fmtDateTime } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import OrderFormDialog from "@/components/OrderFormDialog";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, Loader2, ChevronLeft, ChevronRight, Eye, Share2, Copy, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "collectes", label: "Collectes", status: ["Nouveau", "Collecté"] },
  { key: "traitement", label: "En traitement", status: "En traitement" },
  { key: "traitees", label: "Traitées", view: "traitees" },
  { key: "livraisons", label: "Livraisons", view: "livraisons" },
  { key: "terminees", label: "Terminées", status: "Livré" },
  { key: "annulees", label: "Annulées", status: "Annulée" },
];

const TAB_STATUSES = {
  collectes: ["Nouveau", "Collecté"],
  traitement: ["En traitement"],
  traitees: ["Traité"],
  livraisons: ["Traité", "Dispatché"],
  terminees: ["Livré"],
  annulees: ["Annulée"],
};

export default function Orders() {
  const [tab, setTab] = useState("collectes");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [counts, setCounts] = useState({ all: 0, collectes: 0, livraisons: 0 });

  const activeTab = TABS.find((t) => t.key === tab);

  const plannedInfo = (o) => {
    if (o.status === "Annulée") return { label: "Statut", value: "Annulée", urgency: "none", note: "" };
    if (o.status === "En traitement") {
      if (!o.processing_at) return { label: "Traitement", value: "En cours", urgency: "upcoming", note: "" };
      const start = new Date(o.processing_at);
      const deadline = new Date(start.getTime() + 48 * 3600000);
      const hrs = (Date.now() - start.getTime()) / 3600000;
      let urgency = "upcoming", note = `${Math.max(0, Math.floor(hrs))}h en traitement`;
      if (hrs >= 48) { urgency = "overdue"; note = "48h dépassées"; }
      else if (hrs >= 24) { urgency = "today"; note = "24h+ · attention"; }
      return { label: "Échéance (48h)", value: fmtDateTime(deadline.toISOString()), urgency, note };
    }
    const isDelivery = ["Traité", "Dispatché", "Livré"].includes(o.status);
    let dt = null, label, value;
    if (isDelivery && o.delivery_date) {
      label = "Livraison"; dt = new Date(o.delivery_date); value = fmtDate(o.delivery_date);
    } else if (o.collect_date) {
      label = "Collecte"; dt = new Date(`${o.collect_date}T${o.collect_time || "00:00"}`);
      value = `${fmtDate(o.collect_date)}${o.collect_time ? " · " + o.collect_time : ""}`;
    } else {
      label = isDelivery ? "Livraison" : "Collecte"; value = "—";
    }
    let urgency = "none", note = "";
    if (o.status === "Livré") {
      urgency = "done";
    } else if (dt && !isNaN(dt.getTime())) {
      const now = new Date();
      const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const days = Math.floor((dt - startToday) / 86400000);
      if (days < 0) { urgency = "overdue"; note = "En retard"; }
      else if (days === 0) { urgency = "today"; note = "Aujourd'hui"; }
      else if (days <= 2) { urgency = "soon"; note = `Dans ${days}j`; }
      else { urgency = "upcoming"; }
    }
    return { label, value, urgency, note };
  };

  const urgencyClass = {
    overdue: "bg-red-50 text-red-700 border border-red-200",
    today: "bg-amber-100 text-amber-800 border border-amber-300",
    soon: "bg-amber-50 text-amber-700 border border-amber-200",
    upcoming: "text-slate-600",
    done: "text-slate-400",
    none: "text-slate-400",
  };

  const accentClass = {
    overdue: "border-l-4 border-l-red-500",
    today: "border-l-4 border-l-amber-500",
    soon: "border-l-4 border-l-amber-300",
    upcoming: "border-l-4 border-l-slate-200",
    done: "border-l-4 border-l-emerald-400",
    none: "border-l-4 border-l-slate-200",
  };

  const waNumber = (n) => (n || "").replace(/[^0-9]/g, "");
  const [unseen, setUnseen] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: "15" });
    if (search) params.set("search", search);
    const allowed = TAB_STATUSES[tab] || [];
    if (activeTab.view) {
      params.set("view", activeTab.view);
      if (statusFilter !== "all" && allowed.includes(statusFilter)) params.set("status", statusFilter);
    } else if (statusFilter !== "all" && allowed.includes(statusFilter)) {
      params.set("status", statusFilter);
    } else {
      const s = Array.isArray(activeTab.status) ? activeTab.status : [activeTab.status];
      params.set("status", s.join(","));
    }

    api.get(`/orders?${params.toString()}`).then((r) => {
      setData({ items: r.data.items, total: r.data.total });
    }).finally(() => setLoading(false));
  }, [page, search, statusFilter, tab]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab, statusFilter, search]);
  useEffect(() => { setStatusFilter("all"); }, [tab]);

  const fetchCounts = useCallback(() => {
    api.get("/orders/meta/activity").then((r) => { setCounts(r.data.counts); setUnseen(r.data.unseen || {}); }).catch(() => {});
  }, []);
  useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, 20000);
    return () => clearInterval(id);
  }, [fetchCounts]);
  useEffect(() => {
    api.post("/orders/meta/seen", { tab }).then(fetchCounts).catch(() => {});
  }, [tab, fetchCounts]);

  const pages = Math.max(1, Math.ceil(data.total / 15));

  const bookingLink = `${window.location.origin}/commander`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingLink);
      toast.success("Lien de commande copié");
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };
  const shareWhatsApp = () => {
    const msg = `Bonjour ! Passez votre commande de pressing CLINIT en ligne ici : ${bookingLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Commandes</h1>
          <p className="text-slate-500 text-sm mt-1">Suivi complet du workflow des commandes</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="orders-share-btn" variant="outline" className="flex-1 sm:flex-initial">
                <Share2 className="w-4 h-4 mr-2" /> Lien de commande
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem data-testid="orders-copy-link" onClick={copyLink}>
                <Copy className="w-4 h-4 mr-2" /> Copier le lien
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="orders-share-whatsapp" onClick={shareWhatsApp}>
                <MessageCircle className="w-4 h-4 mr-2" /> Partager sur WhatsApp
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="orders-new-btn" onClick={() => setShowForm(true)} className="flex-1 sm:flex-initial bg-brand-600 hover:bg-brand-700">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle commande
          </Button>
        </div>
      </div>

      <div className="relative">
        <div className="flex gap-1 border-b overflow-x-auto flex-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TABS.map((t) => {
            const c = counts[t.key] || 0;
            return (
              <button
                key={t.key}
                data-testid={`orders-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                  tab === t.key ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
                {c > 0 && (
                  <span data-testid={`orders-tab-badge-${t.key}`} className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${(unseen[t.key] || 0) > 0 ? "bg-amber-400 text-slate-900" : "bg-blue-100 text-blue-700"}`}>
                    {c}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-white to-transparent lg:hidden" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="orders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="N° commande, client, téléphone..." className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="orders-status-filter" className="w-full sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {(TAB_STATUSES[tab] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-2xl">
          <p className="text-slate-400">Aucune commande trouvée</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">N°</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Client</th>
                  <th className="text-left px-4 py-3 font-semibold">Téléphone</th>
                  <th className="text-left px-4 py-3 font-semibold">Adresse</th>
                  <th className="text-left px-4 py-3 font-semibold">Date prévue</th>
                  <th className="text-right px-4 py-3 font-semibold">Total</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.order_number}`} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(o.id)}>
                    <td className="px-4 py-3 font-mono font-semibold text-brand-700">{o.order_number}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-3">{o.client_name}</td>
                    <td className="px-4 py-3 text-slate-500">{o.whatsapp}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px]">
                      <div className="truncate" title={o.address || ""}>{o.address || "—"}</div>
                      {o.commune_name && <div className="text-xs text-slate-400 truncate">{o.commune_name}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {(() => { const p = plannedInfo(o); const badge = ["overdue", "today", "soon"].includes(p.urgency); return (
                        <div>
                          <span className="text-[11px] uppercase tracking-wide text-slate-400">{p.label}</span>
                          <div className={`mt-0.5 ${badge ? `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${urgencyClass[p.urgency]}` : urgencyClass[p.urgency]}`}>
                            {p.value}{p.note && <span className="opacity-80">· {p.note}</span>}
                          </div>
                        </div>
                      ); })()}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{money(o.total)}</td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-4 py-3 text-right"><Eye className="w-4 h-4 text-slate-400 inline" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.items.map((o) => {
              const p = plannedInfo(o);
              const badge = ["overdue", "today", "soon"].includes(p.urgency);
              return (
              <div key={o.id} data-testid={`order-card-${o.order_number}`} className={`bg-white border rounded-2xl overflow-hidden ${accentClass[p.urgency]}`}>
                <div onClick={() => setDetailId(o.id)} className="p-4 cursor-pointer active:bg-slate-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-mono font-bold text-brand-700">{o.order_number}</div>
                      <div className="font-medium mt-0.5">{o.client_name}</div>
                      <div className="text-sm text-slate-500">{o.whatsapp}</div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                  {(o.address || o.commune_name) && (
                    <div className="text-sm text-slate-600 mt-2">
                      {o.address}{o.commune_name ? <span className="text-slate-400"> · {o.commune_name}</span> : null}
                    </div>
                  )}
                  <div className="text-sm mt-1 flex items-center gap-1.5">
                    <span className="text-slate-400">{p.label} prévue :</span>
                    <span className={badge ? `px-2 py-0.5 rounded-full text-xs font-medium ${urgencyClass[p.urgency]}` : "text-slate-700 font-medium"}>
                      {p.value}{p.note && <span className="opacity-80"> · {p.note}</span>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-3 text-sm">
                    <span className="text-slate-500">{fmtDate(o.created_at)}</span>
                    <span className="font-bold">{money(o.total)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 border-t divide-x text-xs font-medium text-slate-600">
                  <a data-testid={`order-card-call-${o.order_number}`} href={`tel:${o.whatsapp}`} onClick={(e) => e.stopPropagation()} className="flex items-center justify-center gap-1.5 py-2.5 active:bg-slate-50">
                    <Phone className="w-4 h-4 text-brand-600" /> Appeler
                  </a>
                  <a data-testid={`order-card-whatsapp-${o.order_number}`} href={`https://wa.me/${waNumber(o.whatsapp)}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center justify-center gap-1.5 py-2.5 active:bg-slate-50">
                    <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
                  </a>
                  <button data-testid={`order-card-view-${o.order_number}`} onClick={(e) => { e.stopPropagation(); setDetailId(o.id); }} className="flex items-center justify-center gap-1.5 py-2.5 active:bg-slate-50">
                    <Eye className="w-4 h-4 text-slate-500" /> Détails
                  </button>
                </div>
              </div>
            ); })}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">{data.total} commande(s)</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orders-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="orders-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </>
      )}

      <OrderFormDialog open={showForm} onOpenChange={setShowForm} onCreated={load} />
      <OrderDetailDialog orderId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load} />
    </div>
  );
}
